import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  MessageSquare,
  ArrowLeft,
  Loader2,
  Eye,
  Check,
  Mail
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMessages() {
  const { token, isAdmin, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !isAdmin()) {
      navigate('/');
      return;
    }
    fetchMessages();
  }, [isAuthenticated, authLoading, showUnreadOnly]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const params = showUnreadOnly ? '?unread_only=true' : '';
      const response = await axios.get(`${API}/admin/messages${params}`, { headers });
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/messages/${messageId}/read`, {}, { headers });
      toast.success('Message marqué comme lu');
      fetchMessages();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-12 w-12 text-black animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated || !isAdmin()) return null;

  const unreadCount = messages.filter(m => !m.read).length;

  return (
    <div className="min-h-screen py-8 bg-gray-50" data-testid="admin-messages">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="mb-8">
            <Link to="/admin" className="inline-flex items-center text-gray-500 hover:text-black mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour au tableau de bord
            </Link>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center">
                  <MessageSquare className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-black">Messages de Contact</h1>
                  <p className="text-gray-500">{messages.length} messages ({unreadCount} non lus)</p>
                </div>
              </div>
              <Button
                variant={showUnreadOnly ? 'default' : 'outline'}
                onClick={() => setShowUnreadOnly(!showUnreadOnly)}
                className={showUnreadOnly ? 'bg-black text-white hover:bg-gray-800' : 'border-gray-300 text-gray-700'}
              >
                Non lus seulement
              </Button>
            </div>
          </div>

          {/* Messages List */}
          <div className="space-y-4">
            {loading ? (
              [...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-32 bg-gray-100" />
              ))
            ) : messages.length === 0 ? (
              <Card className="bg-white border-gray-200">
                <CardContent className="py-12 text-center">
                  <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Aucun message</p>
                </CardContent>
              </Card>
            ) : (
              messages.map((message) => (
                <Card 
                  key={message.id} 
                  className={`bg-white border-gray-200 ${!message.read ? 'border-l-4 border-l-black' : ''}`}
                  data-testid={`message-${message.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-black">{message.name}</h3>
                          {!message.read && (
                            <Badge className="bg-black text-white">Nouveau</Badge>
                          )}
                        </div>
                        <p className="text-gray-500 text-sm flex items-center gap-2">
                          <Mail className="h-3 w-3" />
                          {message.email}
                        </p>
                        <p className="text-gray-400 text-xs mt-1">{formatDate(message.created_at)}</p>
                      </div>
                      {!message.read && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => markAsRead(message.id)}
                          className="border-gray-300 text-gray-700 hover:bg-gray-50"
                        >
                          <Check className="h-4 w-4 mr-2" />
                          Marquer comme lu
                        </Button>
                      )}
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-600 font-medium mb-2">{message.subject}</p>
                      <p className="text-gray-700 whitespace-pre-wrap">{message.message}</p>
                    </div>
                    {message.phone && (
                      <p className="text-gray-500 text-sm mt-3">Téléphone: {message.phone}</p>
                    )}
                    {message.company && (
                      <p className="text-gray-500 text-sm">Entreprise: {message.company}</p>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
